import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, Icon, Label, Select, Text, TextArea, TextInput, useToaster } from '@gravity-ui/uikit';
import { Plus } from '@gravity-ui/icons';
import type { PlatformExpenseCategoryView, PlatformExpenseView } from '@odonix/contracts';
import { useFinanceApi } from '../../lib/api/finance-context.js';
import { TenantsError } from '../../lib/api/tenants.js';
import { Field } from '../../ui/field.js';
import { count, parseRubles, rub, rublesInput } from '../../ui/format.js';
import { mskToday } from '../../ui/period.js';
import { DateInput } from '../../ui/date-input.js';

/**
 * Окна раздела «Расходы»: расход (новый и правка), категории, удаление.
 */

function useRefresh() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['expenses'] }),
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] }),
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] }),
    ]);
  };
}

function errorProps(error: unknown) {
  if (error === null || error === undefined) return {};
  return {
    errorText: error instanceof TenantsError ? error.message : 'Не удалось сохранить. Повторите',
    showError: true,
  };
}

function fieldError(error: unknown, field: string): string | undefined {
  return error instanceof TenantsError ? error.fields?.[field] : undefined;
}

export function useCategories() {
  const api = useFinanceApi();
  return useQuery({ queryKey: ['expense-categories'], queryFn: () => api.categories() });
}

const NEW_CATEGORY = '__new';

export function ExpenseDialog({
  expense,
  onClose,
}: {
  /** Без расхода — новый. */
  expense: PlatformExpenseView | null;
  onClose: () => void;
}) {
  const api = useFinanceApi();
  const refresh = useRefresh();
  const toaster = useToaster();
  const categories = useCategories();
  const active = (categories.data ?? []).filter((category) => category.archivedAt === null);

  const [categoryId, setCategoryId] = useState<string>(expense?.categoryId ?? '');
  const [newCategory, setNewCategory] = useState('');
  const [title, setTitle] = useState(expense?.title ?? '');
  const [rubles, setRubles] = useState(expense === null ? '' : rublesInput(expense.amountMinor));
  const [spentOn, setSpentOn] = useState(expense?.spentOn ?? mskToday());
  const [comment, setComment] = useState(expense?.comment ?? '');

  // Категорий ещё нет — первую заводят прямо здесь, а не отдельным окном.
  const creatingCategory = categoryId === NEW_CATEGORY || (categories.data !== undefined && active.length === 0);
  const amountMinor = parseRubles(rubles);
  const chosenCategory = creatingCategory ? newCategory.trim().length >= 2 : categoryId !== '';
  const valid = chosenCategory && title.trim().length >= 2 && amountMinor !== null && amountMinor > 0 && spentOn !== '';

  const mutation = useMutation({
    mutationFn: async () => {
      const category = creatingCategory ? (await api.createCategory({ name: newCategory.trim() })).id : categoryId;
      if (expense === null) {
        return api.createExpense({
          categoryId: category,
          title: title.trim(),
          amountMinor: amountMinor ?? 0,
          spentOn,
          ...(comment.trim() === '' ? {} : { comment: comment.trim() }),
        });
      }
      return api.updateExpense(expense.id, {
        ...(category === expense.categoryId ? {} : { categoryId: category }),
        ...(title.trim() === expense.title ? {} : { title: title.trim() }),
        ...(amountMinor === expense.amountMinor ? {} : { amountMinor: amountMinor ?? 0 }),
        ...(spentOn === expense.spentOn ? {} : { spentOn }),
        ...(comment.trim() === (expense.comment ?? '') ? {} : { comment: comment.trim() }),
      });
    },
    onSuccess: async (saved) => {
      await refresh();
      toaster.add({
        name: `expense-${saved.id}`,
        title: expense === null ? `Расход ${rub(saved.amountMinor)} записан` : 'Расход изменён',
        theme: 'success',
        autoHiding: 4000,
      });
      onClose();
    },
  });

  const error = mutation.error;

  return (
    <Dialog open onClose={onClose} size="s" aria-label={expense === null ? 'Новый расход' : 'Расход'}>
      <Dialog.Header caption={expense === null ? 'Новый расход' : 'Изменить расход'} />
      <Dialog.Body>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) mutation.mutate();
          }}
        >
          {categories.data !== undefined && active.length === 0 ? (
            <Field label="Категория" required hint="Категорий ещё нет — заведите первую." error={fieldError(error, 'name')}>
              {(id) => <TextInput id={id} value={newCategory} onUpdate={setNewCategory} placeholder="Серверы" autoFocus />}
            </Field>
          ) : (
            <>
              <Field label="Категория" required error={fieldError(error, 'categoryId')}>
                {(id) => (
                  <Select
                    id={id}
                    width="max"
                    placeholder="Выберите категорию"
                    value={categoryId === '' ? [] : [categoryId]}
                    onUpdate={(values) => {
                      setCategoryId(values[0] ?? '');
                    }}
                    options={[
                      ...active.map((category) => ({ value: category.id, content: category.name })),
                      { value: NEW_CATEGORY, content: '+ Новая категория' },
                    ]}
                  />
                )}
              </Field>
              {categoryId === NEW_CATEGORY ? (
                <Field label="Название новой категории" required error={fieldError(error, 'name')}>
                  {(id) => <TextInput id={id} value={newCategory} onUpdate={setNewCategory} autoFocus placeholder="Реклама" />}
                </Field>
              ) : null}
            </>
          )}
          <Field label="Расход" required hint="На что потрачено." error={fieldError(error, 'title')}>
            {(id) => <TextInput id={id} value={title} onUpdate={setTitle} placeholder="Виртуальная машина MWS, октябрь" />}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Сумма, ₽"
              required
              error={rubles !== '' && amountMinor === null ? 'Число, например 12 500,50' : fieldError(error, 'amountMinor')}
            >
              {(id) => <TextInput id={id} value={rubles} onUpdate={setRubles} controlProps={{ inputMode: 'decimal' }} />}
            </Field>
            <Field label="Дата" required error={fieldError(error, 'spentOn')}>
              {(id) => <DateInput id={id} value={spentOn} onUpdate={setSpentOn} />}
            </Field>
          </div>
          <Field label="Комментарий" error={fieldError(error, 'comment')}>
            {(id) => <TextArea id={id} value={comment} onUpdate={setComment} minRows={2} placeholder="Счёт № 1042 от 1 октября" />}
          </Field>
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply={expense === null ? 'Записать' : 'Сохранить'}
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ disabled: !valid }}
        {...errorProps(error)}
      />
    </Dialog>
  );
}

export function DeleteExpenseDialog({ expense, onClose }: { expense: PlatformExpenseView; onClose: () => void }) {
  const api = useFinanceApi();
  const refresh = useRefresh();
  const mutation = useMutation({
    mutationFn: () => api.removeExpense(expense.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} size="s" aria-label="Удалить расход">
      <Dialog.Header caption="Удалить расход" />
      <Dialog.Body>
        <Text color="secondary">
          {`«${expense.title}» на ${rub(expense.amountMinor)} исчезнет из сумм и отчётов. В базе остаётся след: кто и когда удалил.`}
        </Text>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply="Удалить"
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
        propsButtonApply={{ view: 'outlined-danger' }}
        {...errorProps(mutation.error)}
      />
    </Dialog>
  );
}

function CategoryRow({ category }: { category: PlatformExpenseCategoryView }) {
  const api = useFinanceApi();
  const refresh = useRefresh();
  const [name, setName] = useState(category.name);
  const archived = category.archivedAt !== null;

  const rename = useMutation({
    mutationFn: () => api.updateCategory(category.id, { name: name.trim() }),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: () => api.updateCategory(category.id, { archived: !archived }),
    onSuccess: refresh,
  });
  const error = rename.error ?? toggle.error;

  return (
    <li className="flex flex-col gap-1 border-b border-[var(--g-color-line-generic)] py-2.5 last:border-b-0">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <TextInput
            value={name}
            onUpdate={setName}
            disabled={archived}
            controlProps={{ 'aria-label': `Название категории ${category.name}` }}
          />
        </div>
        {name.trim() !== category.name && name.trim().length >= 2 ? (
          <Button
            view="action"
            loading={rename.isPending}
            onClick={() => {
              rename.mutate();
            }}
          >
            Сохранить
          </Button>
        ) : null}
        <Button
          view="flat-secondary"
          loading={toggle.isPending}
          onClick={() => {
            toggle.mutate();
          }}
        >
          {archived ? 'Вернуть' : 'В архив'}
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Text variant="caption-2" color="secondary">
          {`расходов: ${count(category.expensesCount)}`}
        </Text>
        {archived ? (
          <Label size="xs" theme="unknown">
            в архиве
          </Label>
        ) : null}
      </div>
      {error instanceof TenantsError ? (
        <Text variant="caption-2" color="danger">
          {error.fields?.['name'] ?? error.message}
        </Text>
      ) : null}
    </li>
  );
}

export function CategoriesDialog({ onClose }: { onClose: () => void }) {
  const api = useFinanceApi();
  const refresh = useRefresh();
  const categories = useCategories();
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => api.createCategory({ name: name.trim() }),
    onSuccess: async () => {
      setName('');
      await refresh();
    },
  });

  return (
    <Dialog open onClose={onClose} size="m" aria-label="Категории расходов">
      <Dialog.Header caption="Категории расходов" />
      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <Text color="secondary">
            Категорию не удаляют, а убирают в архив: её прежние расходы остаются в отчётах, а новые в неё не заводятся.
          </Text>
          <form
            className="flex items-start gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim().length >= 2) create.mutate();
            }}
          >
            <div className="min-w-0 flex-1">
              <TextInput
                value={name}
                onUpdate={setName}
                placeholder="Новая категория"
                controlProps={{ 'aria-label': 'Новая категория' }}
                {...(create.error instanceof TenantsError
                  ? { validationState: 'invalid' as const, errorMessage: create.error.fields?.['name'] ?? create.error.message }
                  : {})}
              />
            </div>
            <Button view="action" type="submit" loading={create.isPending} disabled={name.trim().length < 2}>
              <Icon data={Plus} size={16} />
              Добавить
            </Button>
          </form>
          {categories.data === undefined ? null : categories.data.length === 0 ? (
            <Text color="secondary">Категорий пока нет.</Text>
          ) : (
            <ul className="flex flex-col">
              {categories.data.map((category) => (
                <CategoryRow key={`${category.id}-${category.name}-${String(category.archivedAt)}`} category={category} />
              ))}
            </ul>
          )}
        </div>
      </Dialog.Body>
      <Dialog.Footer textButtonApply="Готово" onClickButtonApply={onClose} />
    </Dialog>
  );
}
